/**
 * Zod schemas for the benchmark customActions catalog: the Image section's
 * single run. The node benchmark is the orchestrator's (D791) and has no
 * schema here; the history, the synthetic bench and the session replay are
 * gone with it.
 */
import { z } from 'zod'
import type { PipelineStepInputOutput } from '@camstack/types'
import { PipelineStepInputSchema } from '@camstack/types'

// Re-export the underlying type so this file fully owns the public
// surface of the schemas — keeps tsup's DTS portable.
export type { PipelineStepInputOutput }

// ─── Detection Pipeline single run ────────────────────────────────────────

export interface PipelineRunInputShape {
  readonly steps: readonly PipelineStepInputOutput[]
  readonly imageBase64?: string
  readonly referenceImage?: string
  readonly nodeId?: string
  readonly sessionId?: string
  /**
   * Inference-device selector of `nodeId`, `<backend>:<device>` —
   * `openvino:npu`, `edgetpu:usb`, `coreml:all`, `cpu`. Selects WHICH device
   * pool of the node runs the call; omitted ⇒ that node's default pool. The
   * ONLY pool selector: the `engine` override that sat beside it swapped the
   * executor's shared engine state and is gone (D646).
   */
  readonly deviceKey?: string
  /**
   * Execution plane, forwarded verbatim to `pipelineExecutor.runPipeline`.
   * `'full'` (the cap's default) runs root AND crop children in one call;
   * `'frame'` is the live per-frame dispatch — root-plane steps only.
   */
  readonly plane?: 'full' | 'frame'
}

export const PipelineRunInputSchema: z.ZodType<PipelineRunInputShape> = z.object({
  steps: z.array(PipelineStepInputSchema).readonly(),
  imageBase64: z.string().optional(),
  referenceImage: z.string().optional(),
  /** Target node. Forwarded to `pipelineExecutor.runPipeline`. Omit to run on the hub. */
  nodeId: z.string().optional(),
  /** UI correlation id stamped on every `pipeline.progress` line of the run. */
  sessionId: z.string().optional(),
  deviceKey: z.string().optional(),
  plane: z.enum(['full', 'frame']).optional(),
})
